import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

// project import
import {
  useGetBalancesQuery,
  useGetComputedBalancesQuery,
  useGetDailyPositionsQuery,
  useGetBalancesByPortfolioByCurrencyQuery,
  useGetMarginBalancesQuery,
  useGetAccountsBalancesQuery,
} from 'store/api/merklebaseAPI'
import {
  selectBalancesRaw,
  selectAccountsBalancesRaw,
  selectMarginBalancesRaw,
  selectBalancesByPortfolioByCurrencyData,
  selectDailyPositionsData,
  selectBalancesLoading,
  selectAccountsBalancesLoading,
  selectAccountsBalancesFetching,
  selectMarginBalancesLoading,
  selectBalancesByPortfolioByCurrencyLoading,
  selectDailyPositionsLoading,
} from 'store/selectors/balances'
import usePrices from './usePrices'
import { mapBalance, mapComputedBalance, mapMarginBalance } from 'utils/mappers/balances'
import useAccounts from './useAccounts'
import useLiquidityTiers from './useLiquidityTiers'
import usePortfolios from './usePortfolios'

// ----------------------------------------------------------------------

export default function useBalances() {
  const { prices, instrumentsPrices } = usePrices()

  const {
    totalAUM: { data: totalAUM },
  } = usePortfolios()

  const { liquidityTiers } = useLiquidityTiers()
  const {
    selectedPortfolios: { data: selectedPortfolios, isLoading: isPortfoliosLoading },
    portfoliosById: { data: portfoliosById },
  } = usePortfolios()

  // Subscribe to cache (trigger fetch); read via selectors for memoized reads and fewer re-renders
  useGetBalancesQuery()
  useGetAccountsBalancesQuery()
  useGetMarginBalancesQuery()
  useGetBalancesByPortfolioByCurrencyQuery()
  useGetDailyPositionsQuery()

  const balancesRaw = useSelector(selectBalancesRaw)
  const accountsBalancesRaw = useSelector(selectAccountsBalancesRaw)
  const marginBalancesRaw = useSelector(selectMarginBalancesRaw)
  const balancesByPortfoliosByCurrency = useSelector(selectBalancesByPortfolioByCurrencyData)
  const dailyPositions = useSelector(selectDailyPositionsData)

  const isBalancesLoading = useSelector(selectBalancesLoading)
  const isAccountsBalancesLoading = useSelector(selectAccountsBalancesLoading)
  const isAccountsBalancesFetching = useSelector(selectAccountsBalancesFetching)
  const isMarginBalancesLoading = useSelector(selectMarginBalancesLoading)
  const isBalancesByProviderByCurrencyLoading = useSelector(
    selectBalancesByPortfolioByCurrencyLoading
  )
  const isDailyPositionsLoading = useSelector(selectDailyPositionsLoading)

  const {
    data: computedBalances = [],
    isLoading: isComputedBalancesLoading,
    // eslint-disable-next-line no-unused-vars
    isFetching: isComputedBalancesFetching,
  } = useGetComputedBalancesQuery(
    { portfolios: selectedPortfolios.map((p) => p.id) },
    {
      skip: isPortfoliosLoading,
    }
  )


  const {
    accountsById: { data: accountsById, isLoading: isAccountsLoading },
  } = useAccounts()

  const accountsBalancesMapped = useMemo(
    () =>
      accountsBalancesRaw?.map((balance, index) =>
        mapBalance({
          balance,
          index,
          prices,
          liquidityTiers,
          accountsById,
          portfoliosById,
          totalAUM,
          instrumentsPrices,
        })
      ),
    [
      accountsBalancesRaw,
      prices,
      liquidityTiers,
      accountsById,
      portfoliosById,
      totalAUM,
      instrumentsPrices,
    ]
  )

  const balances = useMemo(
    () =>
      balancesRaw?.map((balance, index) =>
        mapBalance({
          balance,
          index,
          prices,
          liquidityTiers,
          accountsById,
          portfoliosById,
          totalAUM,
          instrumentsPrices,
        })
      ),
    [balancesRaw, prices, liquidityTiers, accountsById, portfoliosById, totalAUM, instrumentsPrices]
  )

  const spotBalances = useMemo(
    () => accountsBalancesMapped,
    [accountsBalancesMapped]
  )

  const positionsBalances = useMemo(
    () =>
      balances.filter((balance) => ['Option', 'Future', 'Perpetual', 'CFD'].includes(balance.type)),
    [balances]
  )

  const marginBalances = useMemo(
    () =>
      marginBalancesRaw
        ?.filter((balance) => balance.margin_balance)
        .map((balance, index) =>
          mapMarginBalance({
            balance,
            index,
            prices,
            liquidityTiers,
            accountsById,
            portfoliosById,
          })
        ),
    [marginBalancesRaw, prices, liquidityTiers, accountsById, portfoliosById]
  )

  const computedBalancesMapped = useMemo(
    () =>
      computedBalances
        ?.filter((x) => x.currency !== null)
        .map((balance, index) =>
          mapComputedBalance({ balance, index, prices, accountsById, instrumentsPrices })
        ),
    [accountsById, computedBalances, prices, instrumentsPrices]
  )

  const totalBalances = useMemo(
    () => spotBalances.reduce((acc, balance) => acc + balance.usdValue, 0),
    [spotBalances]
  )

  const balancesByCurrency = useMemo(
    () =>
      balances.reduce((acc, balance) => {
        if (!acc[balance.currency]) {
          acc[balance.currency] = {
            usdValue: 0,
            quantity: 0,
          }
        }

        acc[balance.currency] = {
          usdValue: acc[balance.currency].usdValue + balance.usdValue,
          quantity: acc[balance.currency].quantity + balance.available,
        }

        return acc
      }, {}),
    [balances]
  )

  const balancesByProvider = useMemo(
    () =>
      balances.reduce((acc, balance) => {
        if (!acc) {
          acc = {}
        }
        if (!acc[balance.account?.provider?.ticker]) {
          acc[balance.account?.provider?.ticker] = {
            usdValue: 0,
            quantity: 0,
          }
        }

        acc[balance.account?.provider?.ticker] = {
          usdValue: acc[balance.account?.provider?.ticker].usdValue + balance.usdValue,
          quantity: acc[balance.account?.provider?.ticker].quantity + balance.available,
          ticker: balance.account?.provider?.ticker,
        }

        return acc
      }, {}),
    [balances]
  )

  const getDailyPositions = useCallback(
    async (params) => {
      if (isPortfoliosLoading || isAccountsLoading) return []

      return dailyPositions
    },
    [dailyPositions, isAccountsLoading, isPortfoliosLoading]
  )

  return {
    balances: { data: balances, isLoading: isBalancesLoading /*|| isBalancesFetching*/ },
    spotBalances: {
      data: spotBalances,
      isLoading: isAccountsBalancesFetching || isAccountsBalancesLoading,
    },
    positionsBalances: {
      data: positionsBalances,
      isLoading: isBalancesLoading /*|| isBalancesFetching*/,
    },
    computedBalances: {
      data: computedBalancesMapped,
      isLoading: isComputedBalancesLoading /*|| isComputedBalancesFetching*/,
    },
    dailyPositions: {
      data: dailyPositions,
      isLoading: isDailyPositionsLoading /*|| isDailyPositionsFetching*/,
    },
    totalBalances: {
      data: totalBalances,
      isLoading: isBalancesLoading /*|| isBalancesFetching*/,
    },
    balancesByCurrency: {
      data: balancesByCurrency,
      isLoading: isBalancesLoading /*|| isBalancesFetching*/,
    },
    balancesByProvider: {
      data: balancesByProvider,
      isLoading: isBalancesLoading /*|| isBalancesFetching*/,
    },
    getDailyPositions: {
      data: getDailyPositions,
      isLoading: isAccountsLoading /*|| isPortfoliosLoading*/,
    },
    balancesByPortfoliosByCurrency: {
      data: balancesByPortfoliosByCurrency || {},
      isLoading:
        isBalancesByProviderByCurrencyLoading /*|| isBalancesByProviderByCurrencyFetching*/,
    },
    marginBalances: {
      data: marginBalances,
      isLoading: isMarginBalancesLoading /*|| isMarginBalancesFetching*/,
    },
  }
}
